import { Button, HookedForm, TextField } from '@app/components';
import { useLeaveFeedback } from './LeaveFeedback.hooks';
import AttachIcon from '@app/assets/icons/attach.svg?react';
import { BAD_REVIEW_REASONS } from '@app/utils/consts';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import VerticalLogo from '@app/assets/vertical-logo.svg?react';
import ThankYouImage from '@app/assets/illustrations/thank-you.png';

interface ThumbnailItem {
  url: string;
  type: 'image' | 'video';
  file: File;
}

export const LeaveFeedback = () => {
  const { t } = useTranslation();

  
  const {
    leaveFeedbackForm,
    handleChange,
    thumbnails,
    removeFile,
    handleSubmit,
    handleTagSelection,
    isSubmitting,
    showThankYouCard,
  } = useLeaveFeedback();

  // Create a wrapper function that matches SubmitHandler type
  const handleFormSubmit = (values: FieldValues) => {
    handleSubmit(values);
  };

  const selectedTags = leaveFeedbackForm.watch('tags') || [];

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-4 sm:py-6 gap-4 sm:gap-6 bg-gray-50">
      <div className="flex items-center justify-center gap-2 ">
        <VerticalLogo className="w-32 h-32" />
      </div>
      
      {showThankYouCard ? (
        <div className="bg-white rounded-lg w-full max-w-[845px] py-10 sm:py-16 px-4 sm:px-20 shadow-sm">
          <div className="text-center">
            <img src={ThankYouImage} alt="Thank you" className="w-32 h-auto mx-auto mb-6" />
            <h2 className="font-bold text-2xl sm:text-3xl leading-none text-gray-800 mb-4">
              {t('leaveFeedback.thankYouTitle', 'Thank you for your feedback!')}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              {t('leaveFeedback.thankYouSubtitle', 'Your feedback is incredibly valuable, and we\'ll make sure to incorporate your preferences.')}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg w-full max-w-[845px] py-6 sm:py-8 px-4 sm:px-20 shadow-sm">
          <div className="text-center mb-6">
            <h2 className="font-bold text-2xl sm:text-3xl leading-none text-gray-800 mb-2">
              {t('leaveFeedback.title', 'Your feedback of Echo Ratings')}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              {t('leaveFeedback.subtitle', 'Describe your experience with our platform so that we can take your suggestions into account and make it better.')}
            </p>
          </div>
          <hr className="border-gray-200 mb-6" />
          
          <HookedForm
            formHook={leaveFeedbackForm}
            onSubmit={handleFormSubmit}
            className="flex gap-2 flex-col space-y-6"
          >
          <div>
            <h4 className="text-lg font-bold text-gray-800 mb-4">
              {t('leaveFeedback.selectTags', 'Select the tags that best describe your experience')}
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {BAD_REVIEW_REASONS.map((reason) => {
                const isSelected = selectedTags.includes(reason.value);
                return (
                  <button
                    key={reason.value}
                    type="button"
                    onClick={() => handleTagSelection(reason.value)}
                    className={clsx(
                      'rounded-full border py-1 px-2 text-xs cursor-pointer transition-all text-center',
                      isSelected
                        ? 'bg-blue-50 border-[#52B6FE] text-[#52B6FE] font-medium'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    {t(`leaveFeedback.tags.${reason.value}`, reason.label)}
                  </button>
                );
              })}
            </div>
          </div>



          <div>
            <h4 className="text-lg font-bold text-gray-800 mb-2">
              {t('leaveFeedback.suggestionsTitle', 'What can we add/change to improve your experience?')}
            </h4>
            <TextField
              name="feedback"
              placeholder={t('leaveFeedback.feedbackPlaceholder', 'Describe your ideas')}
              className="min-h-[120px]"
            />
          </div>

          <div>
            <label
              htmlFor="file"
              className="w-fit font-medium text-gray-700 bg-gray-100 p-3 rounded-lg cursor-pointer flex gap-2 items-center hover:bg-gray-200 transition-colors"
            >
              <AttachIcon />
              Attach photos
            </label>
            <p className="text-xs text-gray-500 mt-2">
              {t('leaveFeedback.supportedFormats', 'Supported formats:')} <strong>.jpg, .jpeg, .png, .gif, .webp</strong>
            </p>
            <input
              id="file"
              name="media"
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              multiple
              className="hidden"
              onChange={handleChange}
            />
          </div>

          {thumbnails.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {thumbnails.map((thumb: ThumbnailItem, index: number) => (
                <div
                  key={index}
                  className="w-full aspect-square relative rounded-lg overflow-hidden border border-gray-200"
                >
                  {thumb.type === 'video' ? (
                    <video
                      src={thumb.url}
                      className="absolute w-full h-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={thumb.url}
                      className="absolute w-full h-full object-cover object-center"
                      alt={t('leaveFeedback.mediaAlt', 'Feedback media')}
                    />
                  )}
                  <div className="bg-black/30 inset-0 absolute" />
                  <button
                    onClick={() => removeFile(index)}
                    type="button"
                    className="h-6 w-6 bg-white/80 flex items-center justify-center top-2 right-2 absolute z-10 rounded-full hover:bg-white transition-colors"
                    aria-label={t('leaveFeedback.removeMedia', 'Remove media')}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0"
                    >
                      <path
                        d="M9 3L3 9M9 9L3 3"
                        stroke="black"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg font-medium"
            >
              {isSubmitting
                ? t('leaveFeedback.submitting', 'Sending...')
                : t('leaveFeedback.sendFeedback', 'Send feedback')}
            </Button>
          </div>
        </HookedForm>
        </div>
      )}
    </div>
  );
};
